import { Component } from "react";
import axios from "../../../axios"
import classes from "./overview.css"
import DataTable from "react-data-table-component"
import ProgressBar from "./ProgressBar/ProgressBar"
import ContentContainer from "../../../containers/UI/ContentContainer/ContentContainer"
import Select from "./../../configuration/shared/Select/Select"
import { ReactComponent as Visual } from "./Visual.svg"

class Financial extends Component {

  state = {

    orderAmount: {},
    invoiceAmount: {},
    deferredAmount: 0,
    productMargin: {},
    projectMargin: {},
    customers: {},

    // Adapt each year, possibly dynamically 
    totalTarget: { 2025: 120000, 2026: 125000 },
    totalRange: { 2025: 240000, 2026: 250000 },
    productTarget: { 2025: 60000, 2026: 60000 },
    projectTarget: { 2025: 60000, 2026: 60000 },

    // Multi-year support
    financialYear: new Date().getFullYear().toString(),
    yearOptions: Array.from({ length: new Date().getFullYear() - 2025 + 2 }, (_, i) => 2025 + i).map(year => ({ label: `FY ${year}`, value: year.toString() }))
  }


  customerColumns = [
    { name: "Customer", selector: "name", sortable: true, style: { fontSize: "15px" }},
    { name: "Invoiced margin", selector: "invoicedMargin", format: row => !isNaN(row.invoicedMargin) ? "€ " + row.invoicedMargin.toLocaleString("de-DE") : "", sortable: true, left: true, style: { fontSize: "14px" }},
    { name: "Invoiced revenue", selector: "invoicedRevenue", format: row => !isNaN(row.invoicedRevenue) ? "€ " + row.invoicedRevenue.toLocaleString("de-DE") : "", sortable: true, left: true, style: { fontSize: "14px" }},
    { name: "Total margin", selector: "totalMargin", format: row => !isNaN(row.totalMargin) ? "€ " + row.totalMargin.toLocaleString("de-DE") : "", sortable: true, left: true, style: { fontSize: "14px" }},
    { name: "Total revenue", selector: "totalRevenue", format: row => !isNaN(row.totalRevenue) ? "€ " + row.totalRevenue.toLocaleString("de-DE") : "", sortable: true, left: true, style: { fontSize: "14px" }},
  ]

  componentDidMount() {

    // Fetch financial overview
    axios.get("/orders").then(response => {
      if (response.data) {

        let orders = response.data
        let stats = { orderAmount: {}, invoiceAmount: {}, productMargin: {}, projectMargin: {}, customers: {}, deferredAmount: 0 }

        // Loop over all orders for calculation        
        orders.forEach(order => {

          // Add year if it doesn't exist
          let year = order.financialYear
          if (!(year in stats.orderAmount)) stats.orderAmount[year] = 0
          if (!(year in stats.invoiceAmount)) stats.invoiceAmount[year] = 0
          if (!(year in stats.productMargin)) stats.productMargin[year] = 0
          if (!(year in stats.projectMargin)) stats.projectMargin[year] = 0
          if (!(year in stats.customers)) stats.customers[year] = {}

          // Calculate margin
          let margin = order.totalAmount - order.cogsAmount
          let unpaidAmount = order.totalAmount - order.invoicedAmount
          let marginFraction = (order.totalAmount !== 0) ? (margin / order.totalAmount) : 0

          // Assign paid part
          if (order.targetOnInvoice === true) {

            // If this order's margin is counted based on invoices, assign margin to the correct financial year based on invoice
            Object.entries(order.invoicedPerYear ?? {}).forEach(([invoiceYear, invoiceAmount]) => {
              // Make sure this year exists
              if (!(invoiceYear in stats.invoiceAmount)) stats.invoiceAmount[invoiceYear] = 0
              if (!(invoiceYear in stats.productMargin)) stats.productMargin[invoiceYear] = 0
              if (!(invoiceYear in stats.projectMargin)) stats.projectMargin[invoiceYear] = 0

              // Assign invoiced margin to invoiced amount
              stats.invoiceAmount[invoiceYear] += invoiceAmount * marginFraction

              // Assign invoiced margin to correct category
              if (order.category === "product") {
                stats.productMargin[invoiceYear] += invoiceAmount * marginFraction
              } else if (order.category === "project") {
                stats.projectMargin[invoiceYear] += invoiceAmount * marginFraction
              }
            })
          } else {

            // Otherwise, assign invoiced amount to the fianancial year when the order was placed
            stats.invoiceAmount[year] += order.invoicedAmount * marginFraction

            // Assign margin to correct category
            if (order.category === "product") {
              stats.productMargin[year] += margin
            } else if (order.category === "project") {
              stats.projectMargin[year] += margin
            }
          }

          // Assign unpaid part
          if (order.targetOnInvoice === true) {

            // If this order's margin is counted based on invoices, assign the unpaid part to "deferred"
            stats.deferredAmount += unpaidAmount * marginFraction

          } else {

            // Otherwise, if counted based on order, assign unpaid part to "on order"
            stats.orderAmount[year] += unpaidAmount * marginFraction
          }

          // Make sure a customer entry exists in the order year
          if (!(order.customer.name in stats.customers[year])) stats.customers[year][order.customer.name] = { name: order.customer.name, invoicedMargin: 0, invoicedRevenue: 0, totalMargin: 0, totalRevenue: 0 }

          // Assign the totals in the year of the order
          stats.customers[year][order.customer.name].totalMargin += margin
          stats.customers[year][order.customer.name].totalRevenue += order.totalAmount ?? 0

          // Assign invoiced margin in the correct year
          if (order.targetOnInvoice === true) {

            // Assign to customer in the correct year
            Object.entries(order.invoicedPerYear ?? {}).forEach(([invoiceYear, amount]) => {

              // Make sure this year and this customer entry exist
              if (!(invoiceYear in stats.customers)) stats.customers[invoiceYear] = {}
              if (!(order.customer.name in stats.customers[invoiceYear])) stats.customers[invoiceYear][order.customer.name] = { name: order.customer.name, invoicedMargin: 0, invoicedRevenue: 0, totalMargin: 0, totalRevenue: 0 }
            
              // Assign in the invoiced year
              stats.customers[invoiceYear][order.customer.name].invoicedMargin += amount * marginFraction
              stats.customers[invoiceYear][order.customer.name].invoicedRevenue += amount
            })
          } else {
            // Assign in the order year
            stats.customers[year][order.customer.name].invoicedMargin += order.invoicedAmount * marginFraction
            stats.customers[year][order.customer.name].invoicedRevenue += order.invoicedAmount
          } 
        })

        // Flatten customers
        stats.customers = Object.fromEntries(Object.entries(stats.customers).map(([year, customers]) => [year, Object.values(customers)]))

        // Update in state
        this.setState(stats)

        // Animate target
        // for (let i = 0; i < 40; i++) {
        //   setTimeout(() => {
        //     this.setState({ projectMargin: this.state.projectMargin + 3903/2, orderAmount: this.state.orderAmount + 3903/2 })
        //   }, 2000 + 200*i)
        // }
      }
    })
  }

  render() {

    return (
      <ContentContainer title="Overview" rightContainer={<Select options={this.state.yearOptions} value={this.state.financialYear} onChange={(val) => this.setState({ financialYear: val.value })}/>}>

        <div style={{ width: "100%", display: "flex" }}>
          <div className={classes.visualContainer}>
            <Visual className={classes.backgroundVisual}/>
            <label className={classes.marginLabel}>{"€ " + Math.round((this.state.orderAmount[this.state.financialYear] ?? 0) + (this.state.invoiceAmount[this.state.financialYear] ?? 0)).toLocaleString("de-DE")}</label>
            <label className={classes.orderLabel}>{this.state.orderAmount[this.state.financialYear] ? "€ " + Math.round(this.state.orderAmount[this.state.financialYear]).toLocaleString("de-DE") : "€ 0"}</label>
            <label className={classes.invoiceLabel}>{this.state.invoiceAmount[this.state.financialYear] ? "€ " + Math.round(this.state.invoiceAmount[this.state.financialYear]).toLocaleString("de-DE") : "€ 0"}</label>
            <label className={classes.deferredLabel}>{this.state.deferredAmount ? "€ " + Math.round(this.state.deferredAmount).toLocaleString("de-DE") : "€ 0"}</label>
          </div>
        </div>

        <p className={classes.subtitle} style={{ marginTop: "50px" }}>Target progress</p>
        
        <div className={classes.progressContainer}>
          <ProgressBar
            title={"Total margin"}
            target={this.state.totalTarget[this.state.financialYear]}
            range={this.state.totalRange[this.state.financialYear]}
            value={(this.state.projectMargin[this.state.financialYear] ?? 0) + (this.state.productMargin[this.state.financialYear] ?? 0)}
            markers={6}
            large
            showPercentage
          />
          <ProgressBar
            title={"Product margin"}
            target={this.state.productTarget[this.state.financialYear]}
            range={this.state.productTarget[this.state.financialYear]}
            value={this.state.productMargin[this.state.financialYear] ?? 0}
            markers={6}
            showAmount
          />
          <ProgressBar
            title={"Project margin"}
            target={this.state.projectTarget[this.state.financialYear]}
            range={this.state.projectTarget[this.state.financialYear]}
            value={this.state.projectMargin[this.state.financialYear] ?? 0}
            markers={6}
            showAmount
          />
        </div>

        <p className={classes.subtitle} style={{ marginTop: "70px" }}>Customer orders</p>

        <div className={classes.progressContainer}>
          <DataTable
            columns={this.customerColumns}
            data={this.state.customers[this.state.financialYear]}
            noHeader={true}
            pagination={false}
            highlightOnHover={true}
            striped={true}
            defaultSortField={"totalMargin"}
            defaultSortAsc={false}
            customStyles={{ headCells: { style: { fontWeight: "bold", fontSize: "15px" }}}}
          />
        </div>
      </ContentContainer>
    )
  }
}

export default Financial;
